import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import apiClient from '../../lib/api';
import StripeAccountOnboarding from '../StripeAccountOnboarding';

const StripeConnect = ({ 
  showAsCard = true, 
  showTitle = true,
  onAccountUpdate = () => {} 
}) => {
  const [stripeConnectAccount, setStripeConnectAccount] = useState(null);
  const [stripeAccountError, setStripeAccountError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showEmbeddedOnboarding, setShowEmbeddedOnboarding] = useState(false);
  const [onboardingAccountId, setOnboardingAccountId] = useState(null);
  const [showExistingAccountForm, setShowExistingAccountForm] = useState(false);
  const [existingAccountForm, setExistingAccountForm] = useState({
    accountId: '',
    secretKey: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  // Helper function to determine verification status based on Stripe account data
  const determineVerificationStatus = (accountData) => {
    // Account is verified if:
    // 1. Details have been submitted
    // 2. Charges are enabled
    // 3. Payouts are enabled
    // 4. No past_due requirements
    const hasDetailsSubmitted = accountData.details_submitted === true;
    const hasChargesEnabled = accountData.charges_enabled === true;
    const hasPayoutsEnabled = accountData.payouts_enabled === true;
    const hasNoPastDueRequirements = !accountData.requirements?.past_due || accountData.requirements.past_due.length === 0;
    
    return hasDetailsSubmitted && hasChargesEnabled && hasPayoutsEnabled && hasNoPastDueRequirements 
      ? 'verified' 
      : 'pending';
  };

  useEffect(() => {
    loadStripeConnectAccount();
  }, []);

  const loadStripeConnectAccount = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.getMyStripeConnectAccount();
      
      // Check if there's an error (user has account ID but can't access it)
      if (response.hasAccountId && !response.data) {
        // Set error message to display on page
        if (response.error === 'stripe_not_configured') {
          setStripeAccountError('Stripe Connect is not configured. Please contact an administrator.');
        } else if (response.error === 'account_not_accessible') {
          setStripeAccountError('Unable to access your Stripe account. Please contact an administrator or set up a new account.');
        }
      } else {
        setStripeAccountError(null);
      }
      
      // Process the account data the same way as refreshStripeAccountStatus does
      if (response.data) {
        const processedAccount = {
          id: response.data.id,
          verificationStatus: determineVerificationStatus(response.data),
          payoutsEnabled: response.data.payouts_enabled,
          chargesEnabled: response.data.charges_enabled,
          businessType: response.data.business_type,
          country: response.data.country,
          email: response.data.email,
          createdAt: response.data.created ? new Date(response.data.created * 1000).toISOString() : null
        };
        setStripeConnectAccount(processedAccount);
        onAccountUpdate(processedAccount);
      } else {
        setStripeConnectAccount(null);
        onAccountUpdate(null);
      }
    } catch (error) {
      console.error('Error loading Stripe Connect account:', error);
      setStripeConnectAccount(null);
      setStripeAccountError('Failed to load Stripe account information.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewInStripeDashboard = async () => {
    if (!stripeConnectAccount) return;
    
    try {
      // For verified accounts, use the login link
      const result = await apiClient.createLoginLink(stripeConnectAccount.id);
      const loginUrl = result.loginUrl;
      
      // Open the Stripe Dashboard in a new tab
      window.open(loginUrl, '_blank');
    } catch (error) {
      console.error('Error opening Stripe Dashboard:', error);
      toast.error('Failed to open Stripe Dashboard. Please try again.');
    }
  };

  const refreshStripeAccountStatus = async () => {
    if (!stripeConnectAccount) return;
    
    try {
      const result = await apiClient.getStripeConnectAccount(stripeConnectAccount.id);
      
      const updatedAccount = {
        id: result.data.id,
        verificationStatus: determineVerificationStatus(result.data),
        payoutsEnabled: result.data.payouts_enabled,
        chargesEnabled: result.data.charges_enabled,
        businessType: result.data.business_type,
        country: result.data.country,
        email: result.data.email,
        createdAt: new Date(result.data.created * 1000).toISOString()
      };
      setStripeConnectAccount(updatedAccount);
      onAccountUpdate(updatedAccount);
      toast.success('Account status updated');
    } catch (error) {
      console.error('Error refreshing account status:', error);
      toast.error('Failed to refresh account status');
    }
  };

  const handleEmbeddedOnboardingComplete = (accountId) => {
    toast.success('Account onboarding completed successfully!');
    setShowEmbeddedOnboarding(false);
    setOnboardingAccountId(null);
    
    // Refresh the account status
    loadStripeConnectAccount();
  };

  const handleEmbeddedOnboardingError = (error) => {
    toast.error(`Onboarding failed: ${error}`);
    setShowEmbeddedOnboarding(false);
    setOnboardingAccountId(null);
  };

  const handleLinkExistingAccount = async () => {
    try {
      setIsSaving(true);
      
      // Validate required fields
      if (!existingAccountForm.accountId || !existingAccountForm.secretKey) {
        toast.error('Please fill in all required fields');
        return;
      }

      // Connect existing Stripe account
      const connectData = {
        accountId: existingAccountForm.accountId,
        secretKey: existingAccountForm.secretKey
      };

      const result = await apiClient.connectStripeAccount(connectData);
      const connectedAccount = {
        id: result.data.id,
        verificationStatus: determineVerificationStatus(result.data),
        payoutsEnabled: result.data.payouts_enabled,
        chargesEnabled: result.data.charges_enabled,
        businessType: result.data.business_type,
        country: result.data.country,
        email: result.data.email,
        createdAt: new Date(result.data.created * 1000).toISOString()
      };
      
      setStripeConnectAccount(connectedAccount);
      setShowExistingAccountForm(false);
      onAccountUpdate(connectedAccount);
      
      // Reset form
      setExistingAccountForm({
        accountId: '',
        secretKey: ''
      });
      
      toast.success('Existing Stripe account connected successfully!');
      
    } catch (error) {
      console.error('Error linking existing account:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to link existing account');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Loading Stripe account...
        </Typography>
      </Box>
    );
  }

  const content = (
    <Box>
      {stripeAccountError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {stripeAccountError}
          </Typography>
        </Alert>
      )}
      
      {!stripeConnectAccount ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="h6" gutterBottom>
            Connect Your Stripe Account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set up your Stripe Connect account to start receiving payments for card sales and user engagement.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                setOnboardingAccountId(null); // Will trigger account creation
                setShowEmbeddedOnboarding(true);
              }}
              startIcon={<AddIcon />}
              disabled={isSaving}
              sx={{ minWidth: 200 }}
            >
              {isSaving ? 'Creating...' : 'Create Stripe Account'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => setShowExistingAccountForm(true)}
              startIcon={<CreditCardIcon />}
              sx={{ minWidth: 200 }}
            >
              Link Existing Account
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Create a new Stripe Connect account or link an existing one
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          {/* Add prominent alert for pending verification */}
          {stripeConnectAccount.verificationStatus !== 'verified' && (
            <Alert 
              severity="warning" 
              sx={{ mb: 3, textAlign: 'left' }}
              icon={<WarningIcon />}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Account Verification Pending
              </Typography>
              <Typography variant="body2">
                Your Stripe Connect account is set up but still pending verification. 
                You need to complete the verification process to receive payouts. 
                Click "Complete Setup" below to finish verification.
              </Typography>
            </Alert>
          )}
          
          <Typography variant="h6" gutterBottom>
            Stripe Connect Account Connected
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your Stripe Connect account is linked to this platform.
          </Typography>
          
          {/* Account Status */}
          <Box sx={{ mb: 3 }}>
            <Chip 
              label={stripeConnectAccount.verificationStatus === 'verified' ? 'Verified' : 'Pending Verification'}
              color={stripeConnectAccount.verificationStatus === 'verified' ? 'success' : 'warning'}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Account ID: {stripeConnectAccount.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Business Type: {stripeConnectAccount.businessType || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Country: {stripeConnectAccount.country || 'N/A'}
              </Typography>
            </Box>
          </Box>
          
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={refreshStripeAccountStatus}
              startIcon={<RefreshIcon />}
            >
              Refresh Status
            </Button>
            {stripeConnectAccount.verificationStatus === 'verified' ? (
              <Button
                variant="contained"
                size="small"
                onClick={handleViewInStripeDashboard}
                startIcon={<OpenInNewIcon />}
                title="Open your Stripe Express Dashboard"
              >
                View in Stripe Dashboard
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setOnboardingAccountId(stripeConnectAccount.id);
                  setShowEmbeddedOnboarding(true);
                }}
                startIcon={<CreditCardIcon />}
                title="Complete your Stripe account setup"
              >
                Complete Setup
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Embedded Stripe Onboarding Dialog */}
      <Dialog 
        open={showEmbeddedOnboarding} 
        onClose={() => {
          setShowEmbeddedOnboarding(false);
          setOnboardingAccountId(null);
        }}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCardIcon />
            <Typography variant="h6">Complete Your Stripe Account Setup</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, overflow: 'auto', flex: 1 }}>
          <StripeAccountOnboarding
            accountId={onboardingAccountId}
            onComplete={handleEmbeddedOnboardingComplete}
            onError={handleEmbeddedOnboardingError}
          />
        </DialogContent>
        
        <DialogActions sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setShowEmbeddedOnboarding(false);
              setOnboardingAccountId(null);
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Existing Account Dialog */}
      <Dialog 
        open={showExistingAccountForm} 
        onClose={() => setShowExistingAccountForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCardIcon />
            <Typography variant="h6">Link Existing Stripe Account</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Stripe Account ID"
              value={existingAccountForm.accountId}
              onChange={(e) => setExistingAccountForm(prev => ({ ...prev, accountId: e.target.value }))}
              placeholder="acct_..."
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Secret Key"
              type="password"
              value={existingAccountForm.secretKey}
              onChange={(e) => setExistingAccountForm(prev => ({ ...prev, secretKey: e.target.value }))}
              placeholder="sk_..."
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setShowExistingAccountForm(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLinkExistingAccount}
            disabled={isSaving}
          >
            {isSaving ? 'Linking Account...' : 'Link Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (showAsCard) {
    return (
      <Card>
        <CardHeader
          title={
            showTitle ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCardIcon />
                Stripe Connect Account
              </Box>
            ) : undefined
          }
          subheader={showTitle ? "Connect your Stripe account to receive payments and commissions" : undefined}
        />
        <CardContent>
          {content}
        </CardContent>
      </Card>
    );
  }

  return content;
};

export default StripeConnect;
